import { useEffect, useState } from 'react';
import { Field } from '@/components/Admin/AdminFormFields';
import { Input } from '@/components/ui/input';
import { normaliseSlugInput, slugify, validateSlugForCreate } from '@/util/util_slug';

// The id of a record being created.
//
// Shown only on a create form. The id is the document's name in Firestore, and
// for an experience or a collection it is also the last segment of the public
// URL — neither can be changed afterwards without breaking every link to the
// record and every reference held elsewhere. So this is the one moment it can be
// chosen, and the one moment it is worth showing.
//
// It fills itself in from the name and stops doing so as soon as somebody types
// into it: the suggestion is right nearly always, and the two cases where it is
// not — a name written in Japanese, which slugifies to nothing, and a second
// record with a name the catalogue already uses — are exactly the cases where a
// silently derived id would go wrong.

interface SlugFieldProps {
  /** Field label: 'Web Address' where the record has a public URL, else 'Record ID'. */
  label: string;
  /** The record's name; the id is derived from it until the field is touched. */
  name: string;
  value: string;
  onChange: (slug: string) => void;
  /** Whether another record in this collection already holds the id. */
  isTaken: (slug: string) => Promise<boolean>;
  /** Where the record will live, e.g. `/experiences/`. Omitted when it has no public page. */
  pathPrefix?: string;
  /** What the id names, for the message when one is taken: 'experience', 'guide'. */
  entityLabel: string;
  error: string | null;
  onErrorChange: (error: string | null) => void;
}

export function SlugField({
  label,
  name,
  value,
  onChange,
  isTaken,
  pathPrefix,
  entityLabel,
  error,
  onErrorChange,
}: SlugFieldProps) {
  const [touched, setTouched] = useState(false);
  const [checking, setChecking] = useState(false);

  // Track the name until somebody edits the id themselves. Writing the derived
  // value back through `onChange` (rather than only rendering it) keeps the form
  // the single source of truth, so submit reads the same string the field shows.
  useEffect(() => {
    if (touched) return;
    const derived = slugify(name);
    if (derived !== value) onChange(derived);
  }, [name, touched, value, onChange]);

  async function handleBlur() {
    const slug = slugify(value);
    if (slug !== value) onChange(slug);
    // A blank field on an untouched form is the name not having produced one
    // yet, not a mistake to point at.
    if (!slug && !touched) return onErrorChange(null);
    setChecking(true);
    onErrorChange(await validateSlugForCreate(slug, isTaken, entityLabel));
    setChecking(false);
  }

  return (
    <Field label={label} required className='tw:sm:col-span-2'>
      <div className='tw:flex tw:items-center tw:gap-1'>
        {pathPrefix && (
          <span className='tw:shrink-0 tw:text-sm tw:text-muted-foreground'>{pathPrefix}</span>
        )}
        <Input
          value={value}
          onChange={(e) => {
            setTouched(true);
            onErrorChange(null);
            onChange(normaliseSlugInput(e.target.value));
          }}
          onBlur={handleBlur}
          aria-invalid={error ? true : undefined}
          className={error ? 'tw:border-destructive' : undefined}
        />
      </div>
      {error
        ? <p className='tw:mt-1 tw:text-xs tw:text-destructive'>{error}</p>
        : (
            <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
              {checking
                ? 'Checking…'
                : `Taken from the name, and yours to change — but only now. It cannot be edited once the ${entityLabel} exists, since everything pointing at it would break.`}
            </p>
          )}
    </Field>
  );
}
